import { DateRangePicker as OriginalDateRangePicker } from "@wojtekmaj/react-daterange-picker";
import { useContext, useState, useEffect, useRef } from "react";
import moment from "moment-timezone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays } from "@fortawesome/free-regular-svg-icons";
import { FilterContext } from "./filters/DashboardFilterContextProvider.jsx";
import {useTranslation} from "react-i18next";

const REPORTING_TIME_ZONE = "Europe/Prague";

export function getPragueTodayAsLocalDate(now = new Date()) {
    const pragueToday = moment(now).tz(REPORTING_TIME_ZONE);

    return new Date(
        pragueToday.year(),
        pragueToday.month(),
        pragueToday.date()
    );
}

export function clampDateRangeToToday(dateRange, today = getPragueTodayAsLocalDate()) {
    if (!Array.isArray(dateRange) || dateRange.length !== 2 || dateRange.some((date) => !(date instanceof Date))) {
        return null;
    }

    const maxTimestamp = today.getTime();
    const didClampFutureDate = dateRange.some((date) => date.getTime() > maxTimestamp);
    const clampedDates = dateRange.map((date) => (
        new Date(Math.min(date.getTime(), maxTimestamp))
    ));

    return !didClampFutureDate || clampedDates[0] <= clampedDates[1]
        ? clampedDates
        : [clampedDates[1], clampedDates[0]];
}

export function isValidDateSegments(day, month, year) {
    const numericDay = Number(day);
    const numericMonth = Number(month);
    const numericYear = Number(year);

    if (!Number.isInteger(numericDay) || !Number.isInteger(numericMonth) || !Number.isInteger(numericYear)) {
        return false;
    }

    const date = new Date(numericYear, numericMonth - 1, numericDay);

    return numericYear >= 1
        && date.getFullYear() === numericYear
        && date.getMonth() === numericMonth - 1
        && date.getDate() === numericDay;
}

export function getCalendarMonthStart(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function getQuickRange(type, now = new Date()) {
    const today = moment(getPragueTodayAsLocalDate(now));
    let startDate, endDate;

    switch (type) {
        case "today":
            startDate = today.clone().startOf("day");
            endDate = today.clone().endOf("day");
            break;
        case "yesterday":
            startDate = today.clone().subtract(1, "days").startOf("day");
            endDate = today.clone().subtract(1, "days").endOf("day");
            break;
        case "last7Days":
            startDate = today.clone().subtract(6, "days").startOf("day");
            endDate = today.clone().endOf("day");
            break;
        case "last14Days":
            startDate = today.clone().subtract(13, "days").startOf("day");
            endDate = today.clone().endOf("day");
            break;
        case "last30Days":
            startDate = today.clone().subtract(29, "days").startOf("day");
            endDate = today.clone().endOf("day");
            break;
        case "thisMonth":
            startDate = today.clone().startOf("month");
            endDate = today.clone().endOf("day");
            break;
        case "lastMonth":
            startDate = today.clone().subtract(1, "months").startOf("month");
            endDate = today.clone().subtract(1, "months").endOf("month");
            break;
        case "last90Days":
            startDate = today.clone().subtract(89, "days").startOf("day");
            endDate = today.clone().endOf("day");
            break;
        default:
            return null;
    }

    return [startDate, endDate];
}

export default function DateRangePicker() {
    let {t} = useTranslation();

    const {
        since, setSince,
        until, setUntil,
    } = useContext(FilterContext);

    const [calendarOpen, setCalendarOpen] = useState(false);
    const [inputRevision, setInputRevision] = useState(0);
    const [calendarActiveStartDate, setCalendarActiveStartDate] = useState(
        getCalendarMonthStart(until.toDate())
    );
    const calendarRef = useRef(null);
    const pendingManualDateRangeRef = useRef(null);
    const isManualInputFocusedRef = useRef(false);
    const today = getPragueTodayAsLocalDate();
    const untilTimestamp = until.valueOf();

    useEffect(() => {
        setCalendarActiveStartDate(getCalendarMonthStart(until.toDate()));
    }, [untilTimestamp]);

    const applyDateRange = (dateRange) => {
        setSince(moment(dateRange[0]));
        setUntil(moment(dateRange[1]));
        setCalendarActiveStartDate(getCalendarMonthStart(dateRange[1]));
    };

    const setQuickRange = (type) => {
        const dateRange = getQuickRange(type);
        if (!dateRange) {
            return;
        }
        const [startDate, endDate] = dateRange;

        pendingManualDateRangeRef.current = null;
        setSince(startDate);
        setUntil(endDate);
        setCalendarActiveStartDate(getCalendarMonthStart(endDate.toDate()));
        setCalendarOpen(false);
    };

    const openCalendarFromContainer = (event) => {
        if (event.target.closest(".react-daterange-picker__calendar, button")) {
            return;
        }

        if (event.target.closest("input")) {
            setCalendarOpen(true);
            return;
        }

        setCalendarOpen((isOpen) => !isOpen);
    };

    const handleDateRangeChange = (dateRange) => {
        const clampedDateRange = clampDateRangeToToday(dateRange, today);
        if (!clampedDateRange) {
            return;
        }

        if (isManualInputFocusedRef.current) {
            pendingManualDateRangeRef.current = clampedDateRange;
            return;
        }

        pendingManualDateRangeRef.current = null;
        applyDateRange(clampedDateRange);
    };

    const commitPendingManualDateRange = (event) => {
        if (event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) {
            return;
        }

        isManualInputFocusedRef.current = false;

        const inputGroups = event.currentTarget.querySelectorAll(".react-daterange-picker__inputGroup");
        const hasInvalidManualDate = [...inputGroups].some((inputGroup) => !isValidDateSegments(
            inputGroup.querySelector(".react-daterange-picker__inputGroup__day")?.value,
            inputGroup.querySelector(".react-daterange-picker__inputGroup__month")?.value,
            inputGroup.querySelector(".react-daterange-picker__inputGroup__year")?.value,
        ));

        if (hasInvalidManualDate) {
            pendingManualDateRangeRef.current = null;
            setInputRevision((revision) => revision + 1);
            return;
        }

        if (pendingManualDateRangeRef.current) {
            applyDateRange(pendingManualDateRangeRef.current);
            pendingManualDateRangeRef.current = null;
        }
    };

    const trackManualInputFocus = (event) => {
        isManualInputFocusedRef.current = event.target.dataset.input === "true";
    };

    // Add custom buttons to `react-calendar__navigation`
    useEffect(() => {
        if (calendarRef.current) {
            const navigation = calendarRef.current.querySelector(
                ".react-calendar__navigation"
            );
            if (navigation && !navigation.querySelector(".custom-buttons")) {
                const customButtons = document.createElement("div");
                customButtons.className = "custom-buttons flex flex-wrap gap-x-1 gap-y-0.5 p-1";

                const buttons = [
                    { label: t('date.label.today'), onClick: () => setQuickRange("today") },
                    { label: t('date.label.yesterday'), onClick: () => setQuickRange("yesterday") },
                    { label: t('date.label.thisMonth'), onClick: () => setQuickRange("thisMonth") },
                    { label: t('date.label.lastMonth'), onClick: () => setQuickRange("lastMonth") },
                    { label: t('date.label.last7Days'), onClick: () => setQuickRange("last7Days") },
                    { label: t('date.label.last14Days'), onClick: () => setQuickRange("last14Days") },
                    { label: t('date.label.last30Days'), onClick: () => setQuickRange("last30Days") },
                    { label: t('date.label.last90Days'), onClick: () => setQuickRange("last90Days") },
                ];

                buttons.forEach((btn) => {
                    const button = document.createElement("button");
                    button.className =
                        "btn bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-slate-600 px-2 py-1 rounded " +
                        "border-gray-300 hover:border-gray-500 text-xs";
                    button.innerText = btn.label;
                    button.onclick = btn.onClick;
                    customButtons.appendChild(button);
                });

                navigation.append(customButtons);
            }
        }
    }, [calendarOpen]);

    return (
        <OriginalDateRangePicker
            key={inputRevision}
            className={
                "btn cursor-pointer bg-white text-gray-600 border-gray-400 hover:text-black hover:border-violet-500 focus-within:text-black focus-within:border-violet-500 focus-within:ring-2 focus-within:ring-violet-100 dark:text-white dark:bg-transparent dark:border-gray-500 dark:hover:border-violet-400 dark:focus-within:border-violet-400 dark:focus-within:ring-violet-900"
            }
            value={[since.toDate(), until.toDate()]}
            isOpen={calendarOpen}
            onClick={openCalendarFromContainer}
            onFocus={trackManualInputFocus}
            onBlur={commitPendingManualDateRange}
            onChange={handleDateRangeChange}
            openCalendarOnFocus={false}
            onCalendarOpen={() => setCalendarOpen(true)}
            onCalendarClose={() => setCalendarOpen(false)}
            clearIcon={null}
            calendarIcon={<FontAwesomeIcon icon={faCalendarDays} />}
            calendarProps={{
                activeStartDate: calendarActiveStartDate,
                inputRef: calendarRef,
                maxDate: today,
                onActiveStartDateChange: ({activeStartDate}) => setCalendarActiveStartDate(activeStartDate),
                prev2Label: null,
                next2Label: null,
            }}
        />
    );
}
